import { useEffect } from 'react'

import { useAuthStore } from '@/features/auth/stores/auth.store'
import { syncAllAdminData } from '@/shared/lib/crmSync'
import { toast } from '@/shared/lib/toast'
import socketService from '@/shared/socket/socket.service'
import { useNotificationStore } from '@/shared/stores/notification.store'

import { useAdminDashboardStore } from '../stores/dashboard.store'
import { useLeadsStore } from '../stores/leads.store'

export const useAdminLeadNotifications = () => {
  const role = useAuthStore((state) => state.user?.role)

  const updateLeadFollowUp = useLeadsStore((state) => state.updateLeadFollowUp)

  useEffect(() => {
    if (role !== 'admin') {
      return undefined
    }

    // Fetch persisted notifications on mount
    void useNotificationStore.getState().fetchNotifications?.()

    const socket = socketService.connect()

    if (!socket) {
      return undefined
    }

    const handleFollowUpConfirmed = (data) => {
      console.log('✅ FOLLOW-UP CONFIRMED:', data)

      updateLeadFollowUp(data.leadId, data)
      useAdminDashboardStore.getState().updateLeadInNotFollowedUp(data.leadId, {
        followUpConfirmed: true,
        followUpConfirmedAt: data.followUpConfirmedAt,
      })

      const title = data.title || 'Follow-up Confirmed'
      const body =
        data.body ||
        (data.leadName
          ? `Sales confirmed follow-up for lead "${data.leadName}".`
          : 'Sales confirmed follow-up for a lead.')

      useNotificationStore.getState().addNotification({
        id: data.id || `follow-up-confirmed-${data.leadId}-${Date.now()}`,
        title,
        body,
        type: 'follow_up_confirmed',
        data: {
          leadId: data.leadId,
        },
        read: false,
        createdAt: data.createdAt || new Date().toISOString(),
      })

      toast.info(body)
      syncAllAdminData({ type: 'follow_up_confirmed', leadId: data.leadId })
    }

    const handleLeadCreated = (data) => {
      console.log('✅ NEW LEAD CREATED FOR ADMIN:', data)

      const title = 'New Lead Created'
      const body = `${data.creatorName || 'A user'} created a new lead: "${data.leadName}".`

      useNotificationStore.getState().addNotification({
        id: data.id || `lead-created-${data.leadId}-${Date.now()}`,
        title,
        body,
        type: 'lead_created',
        data: {
          leadId: data.leadId,
        },
        read: false,
        createdAt: data.createdAt || new Date().toISOString(),
      })

      toast.info(body)
      syncAllAdminData({ type: 'lead_created', leadId: data.leadId })
    }

    const handleLeadAssigned = (data) => {
      console.log('✅ LEAD ASSIGNED EVENT FOR ADMIN:', data)
      syncAllAdminData({ type: 'lead:assigned', leadId: data?.leadId })
    }

    const handleLeadUpdated = (data) => {
      console.log('✅ LEAD UPDATED EVENT FOR ADMIN:', data)
      syncAllAdminData({ type: 'lead:updated', leadId: data?.leadId })
    }

    const handleQuotationCreated = (data) => {
      console.log('✅ QUOTATION CREATED EVENT FOR ADMIN:', data)
      syncAllAdminData({
        type: 'quotation:created',
        quotationId: data?.quotationId,
      })
    }

    const handleQuotationSent = (data) => {
      console.log('✅ QUOTATION SENT EVENT FOR ADMIN:', data)
      syncAllAdminData({
        type: 'quotation:sent',
        quotationId: data?.quotationId,
      })
    }

    const handleNotificationNew = (data) => {
      if (!data) return
      useNotificationStore.getState().addNotification(data)
      if (data.type === 'follow_up_confirmed') {
        if (data.data?.leadId) {
          updateLeadFollowUp(data.data.leadId, { followUpConfirmed: true })
          useAdminDashboardStore
            .getState()
            .updateLeadInNotFollowedUp(data.data.leadId, {
              followUpConfirmed: true,
            })
        }
        toast.info(data.body || data.title)
        syncAllAdminData({ type: 'follow_up_confirmed' })
      } else if (
        data.type === 'lead_created' ||
        data.type === 'leads_uploaded' ||
        data.type === 'quotation_sent' ||
        data.type === 'quotation_created' ||
        data.type === 'message'
      ) {
        toast.info(data.body || data.title)
        syncAllAdminData({ type: data.type })
      }
    }

    socket.on('lead:follow-up-confirmed', handleFollowUpConfirmed)
    socket.on('lead:created', handleLeadCreated)
    socket.on('lead:assigned', handleLeadAssigned)
    socket.on('lead:updated', handleLeadUpdated)
    socket.on('quotation:created', handleQuotationCreated)
    socket.on('quotation:sent', handleQuotationSent)
    socket.on('notification:new', handleNotificationNew)

    return () => {
      socket.off('lead:follow-up-confirmed', handleFollowUpConfirmed)
      socket.off('lead:created', handleLeadCreated)
      socket.off('lead:assigned', handleLeadAssigned)
      socket.off('lead:updated', handleLeadUpdated)
      socket.off('quotation:created', handleQuotationCreated)
      socket.off('quotation:sent', handleQuotationSent)
      socket.off('notification:new', handleNotificationNew)
    }
  }, [role, updateLeadFollowUp])
}
